import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Pages/SupportandComunities.css';

/* ══════════════════════════════════════
   GSAP LOADER
══════════════════════════════════════ */
function loadGSAP() {
  return new Promise((resolve) => {
    if (window.gsap && window.ScrollTrigger) { resolve(); return; }
    const s1 = document.createElement('script');
    s1.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
    s1.onload = () => {
      const s2 = document.createElement('script');
      s2.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js';
      s2.onload = () => { window.gsap.registerPlugin(window.ScrollTrigger); resolve(); };
      document.head.appendChild(s2);
    };
    document.head.appendChild(s1);
  });
}

/* ══════════════════════════════════════
   PARTICLE CANVAS
══════════════════════════════════════ */
function ParticleField() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d'); let W, H, pts, raf;
    const resize = () => { W = c.width = c.offsetWidth; H = c.height = c.offsetHeight; };
    const make = () => ({ x: Math.random() * W, y: Math.random() * H, vx: (Math.random() - .5) * .25, vy: (Math.random() - .5) * .25, r: Math.random() * 1.3 + .3, a: Math.random() * .35 + .07 });
    const init = () => { resize(); pts = Array.from({ length: 55 }, make); };
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      pts.forEach(p => { p.x += p.vx; p.y += p.vy; if (p.x < 0) p.x = W; if (p.x > W) p.x = 0; if (p.y < 0) p.y = H; if (p.y > H) p.y = 0; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fillStyle = `rgba(47,128,237,${p.a})`; ctx.fill(); });
      for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y, d = Math.sqrt(dx * dx + dy * dy);
        if (d < 90) { ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y); ctx.strokeStyle = `rgba(47,128,237,${.1 * (1 - d / 90)})`; ctx.lineWidth = .5; ctx.stroke(); }
      }
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    window.addEventListener('resize', init);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', init); };
  }, []);
  return <canvas ref={ref} className="sc-canvas" aria-hidden />;
}

/* ══════════════════════════════════════
   SECTION HEADER
══════════════════════════════════════ */
function SectionHeader({ eyebrow, title, accent, sub }) {
  return (
    <div className="sc-section-header">
      {eyebrow && <span className="sc-eyebrow">{eyebrow}</span>}
      <h2 className="sc-section-title">{title} {accent && <span className="sc-accent">{accent}</span>}</h2>
      {sub && <p className="sc-section-sub">{sub}</p>}
    </div>
  );
}

/* ══════════════════════════════════════
   SVG ICONS
══════════════════════════════════════ */
const IconTicket = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="12" width="36" height="24" rx="4" stroke="currentColor" strokeWidth="2"/>
    <path d="M6 22h6a4 4 0 0 0 0-8H6M42 22h-6a4 4 0 0 1 0-8h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 24h16M16 30h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconChat = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 10h32a2 2 0 0 1 2 2v20a2 2 0 0 1-2 2H14l-8 6V12a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M16 22h16M16 28h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="16" cy="22" r="1.5" fill="currentColor" opacity="0.4"/>
  </svg>
);
const IconKnowledge = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 8h28a2 2 0 0 1 2 2v28a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="2"/>
    <path d="M16 18h16M16 24h16M16 30h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="36" cy="36" r="6" fill="currentColor" opacity="0.15" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M34 36l1.5 1.5L38 34" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconForum = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="18" r="6" stroke="currentColor" strokeWidth="2"/>
    <circle cx="32" cy="18" r="6" stroke="currentColor" strokeWidth="2"/>
    <path d="M6 40c0-5.523 4.477-10 10-10s10 4.477 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M28 40c0-5.523 4.477-10 10-10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M22 40h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
  </svg>
);
const IconWebinar = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="8" width="32" height="22" rx="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M36 17l8-5v16l-8-5V17z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M12 38h16M20 30v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="14" cy="19" r="3" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M20 22l6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);
const IconIdea = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 6a12 12 0 0 1 8 20.9V34H16v-7.1A12 12 0 0 1 24 6z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M18 38h12M20 42h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M24 12v4M16.5 14.5l2.8 2.8M31.5 14.5l-2.8 2.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="24" cy="22" r="2.5" fill="currentColor" opacity="0.4"/>
  </svg>
);
const IconChampion = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 6l4.5 9 10 1.5-7.2 7 1.7 10L24 29l-9 4.5 1.7-10L9.5 16.5 19.5 15z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M16 40h16M20 36v4M28 36v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="24" cy="18" r="3" fill="currentColor" opacity="0.3"/>
  </svg>
);
const IconPartner = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 20a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM34 20a6 6 0 1 0 0-12 6 6 0 0 0 0 12z" stroke="currentColor" strokeWidth="2"/>
    <path d="M4 42c0-5.523 4.477-10 10-10M44 42c0-5.523-4.477-10-10-10M20 32a12 12 0 0 1 8 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M24 30v6M21 33h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconEmail = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="10" width="36" height="28" rx="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M6 14l18 12L42 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconPhone = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 6h6l3 9-4 2a22 22 0 0 0 12 12l2-4 9 3v6c0 2-2 4-4 4C18 38 10 30 10 10c0-2 2-4 4-4z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M32 6a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
    <path d="M32 12a4 4 0 0 1 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
  </svg>
);
const IconLocation = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 4a14 14 0 0 1 14 14c0 10-14 26-14 26S10 28 10 18A14 14 0 0 1 24 4z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <circle cx="24" cy="18" r="5" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
const SupportCommunity = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    loadGSAP().then(() => {
      const { gsap, ScrollTrigger } = window;

      /* hero */
      gsap.fromTo('.sc-hero-content > *',
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out', delay: 0.2 }
      );

      /* section headers */
      gsap.utils.toArray('.sc-section-header').forEach(el => {
        gsap.fromTo(el,
          { opacity: 0, y: 32 },
          { opacity: 1, y: 0, duration: 0.65, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 85%' }
          }
        );
      });

      /* support cards */
      gsap.fromTo('.sc-support-card',
        { opacity: 0, y: 50, scale: 0.96 },
        { opacity: 1, y: 0, scale: 1, duration: 0.65, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: '.sc-support-grid', start: 'top 80%' }
        }
      );

      /* community cards */
      gsap.utils.toArray('.sc-community-card').forEach((el, i) => {
        gsap.fromTo(el,
          { opacity: 0, x: i % 2 === 0 ? -40 : 40 },
          { opacity: 1, x: 0, duration: 0.65, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 82%' }
          }
        );
      });

      /* program cards */
      gsap.fromTo('.sc-program-card',
        { opacity: 0, y: 40, scale: 0.95 },
        { opacity: 1, y: 0, scale: 1, duration: 0.6, stagger: 0.12, ease: 'back.out(1.3)',
          scrollTrigger: { trigger: '.sc-programs-grid', start: 'top 80%' }
        }
      );

      /* contact cards */
      gsap.fromTo('.sc-contact-card',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.55, stagger: 0.09, ease: 'power3.out',
          scrollTrigger: { trigger: '.sc-contact-grid', start: 'top 82%' }
        }
      );

      /* stat counters */
      gsap.utils.toArray('.sc-stat-num').forEach(el => {
        const target = el.dataset.target;
        if (!isNaN(target)) {
          gsap.from(el, {
            textContent: 0,
            duration: 1.8,
            ease: 'power2.out',
            snap: { textContent: 1 },
            scrollTrigger: { trigger: el, start: 'top 88%' },
            onUpdate() { el.textContent = Math.round(+el.textContent) + (el.dataset.suffix || ''); }
          });
        }
      });

      /* cta */
      gsap.fromTo('.sc-cta-inner > *',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, stagger: 0.12, ease: 'power3.out',
          scrollTrigger: { trigger: '.sc-cta-section', start: 'top 82%' }
        }
      );
    });

    return () => {
      if (window.ScrollTrigger) window.ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  const supportCards = [
    {
      icon: <IconTicket />,
      color: 'var(--blue)',
      title: 'Technical Support',
      desc: 'Need help with integration, APIs, or workflow setup? Our tech support engineers are here to troubleshoot and guide you through.',
      items: [
        { icon: '🕐', text: '24/7 Ticket Support' },
        { icon: '💬', text: 'Live Chat (M–F, 9am–6pm IST)' },
        { icon: '📧', text: 'support@incogniflexservices.com' },
        { icon: '⚡', text: 'Priority SLAs for Enterprise Clients' },
      ],
    },
    {
      icon: <IconKnowledge />,
      color: 'var(--cyan)',
      title: 'Knowledge Base',
      desc: 'Find solutions fast with our growing library of guides, references, and best practices.',
      items: [
        { icon: '📖', text: 'Setup guides' },
        { icon: '🔌', text: 'API documentation' },
        { icon: '🎬', text: 'Product walkthroughs' },
        { icon: '🔧', text: 'Troubleshooting tips' },
        { icon: '🧠', text: 'AI & HaaS best practices' },
      ],
      cta: { label: 'Browse the Help Center', to: '/docs' },
    },
  ];

  const communityCards = [
    {
      icon: <IconForum />,
      color: 'var(--purple)',
      title: 'Online Forums',
      desc: 'Collaborate, learn, and connect with other innovators using IncogniFlex solutions. Ask questions, share workflows, and swap ideas.',
      cta: { label: 'Join the Community Forum', to: '/community' },
    },
    {
      icon: <IconWebinar />,
      color: 'var(--amber)',
      title: 'Webinars & Live Sessions',
      desc: 'From product updates to AI/ops workshops — we run regular events to keep you ahead of the curve.',
      cta: { label: 'See Upcoming Events', to: '/events' },
    },
    {
      icon: <IconIdea />,
      color: 'var(--green)',
      title: 'Feature Requests',
      desc: 'We build with you, not just for you. Submit your ideas, vote on features, and help shape our roadmap.',
      cta: { label: 'Submit a Feature Request', to: '/feedback' },
    },
  ];

  const programCards = [
    {
      icon: <IconChampion />,
      color: 'var(--amber)',
      badge: '🎓',
      title: 'IncogniFlex Champions',
      sub: 'Ambassador Program',
      desc: 'Our ambassador program for expert users who want to share knowledge, run meetups, and shape the future of AI+HaaS.',
      highlights: ['Run local & online meetups', 'Early access to features', 'Shape the product roadmap', 'Exclusive champion perks'],
    },
    {
      icon: <IconPartner />,
      color: 'var(--cyan)',
      badge: '🤝',
      title: 'Partner Network',
      sub: 'Integration & Reseller',
      desc: 'Looking to integrate IncogniFlex into your own platform or resell services? Let\'s collaborate and grow together.',
      highlights: ['Co-sell opportunities', 'Technical integration support', 'Revenue sharing model', 'Joint go-to-market'],
      cta: { label: 'Become a Partner', to: '/partners' },
    },
  ];

  const stats = [
    { num: '24', suffix: '/7', label: 'Support Availability' },
    { num: '500', suffix: '+', label: 'Help Articles' },
    { num: '98', suffix: '%', label: 'CSAT Score' },
    { num: '< 2hr', suffix: '', label: 'Avg First Response', raw: true },
  ];

  return (
    <div className="sc-page" ref={pageRef}>

      {/* ════════════════════ HERO ════════════════════ */}
      <section className="sc-hero">
        <ParticleField />
        <div className="sc-grid-bg" aria-hidden />
        <div className="sc-glow sc-glow-l" aria-hidden />
        <div className="sc-glow sc-glow-r" aria-hidden />
        <div className="sc-hero-inner">
          <div className="sc-hero-content">
            <span className="sc-hero-eyebrow">
              <span className="sc-eyebrow-dot" />
              Support & Community · IncogniFlex Services
            </span>
            <h1 className="sc-hero-title">
              We're With You —{' '}
              <span className="sc-hero-accent">Every Step</span>
            </h1>
            <p className="sc-hero-sub">
              At IncogniFlex Services, we don't just deliver technology. We deliver
              partnerships. From day one and every day after — your success is our success.
            </p>
            <div className="sc-hero-tags">
              {['24/7 Support', 'Knowledge Base', 'Community Forums', 'Live Events'].map(t => (
                <span key={t} className="sc-hero-tag">{t}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="sc-hero-cut" aria-hidden />
      </section>

      {/* ════════════════════ STATS BAR ════════════════════ */}
      <section className="sc-stats-section">
        <div className="sc-container">
          <div className="sc-stats-grid">
            {stats.map((s, i) => (
              <div key={i} className="sc-stat">
                <div className="sc-stat-num" data-target={s.raw ? null : s.num} data-suffix={s.suffix}>
                  {s.num}{s.suffix}
                </div>
                <div className="sc-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ SUPPORT ════════════════════ */}
      <section className="sc-support-section">
        <div className="sc-container">
          <SectionHeader
            eyebrow="🛠️ Support You Can Count On"
            title="Always Here,"
            accent="Always Responsive"
            sub="Technical experts and a rich knowledge base — everything you need to move fast and stay unblocked."
          />
          <div className="sc-support-grid">
            {supportCards.map((card, i) => (
              <div key={i} className="sc-support-card" style={{ '--cc': card.color }}>
                <div className="sc-support-card-top">
                  <div className="sc-card-icon-wrap" style={{ borderColor: card.color + '44', background: card.color + '15', color: card.color }}>
                    {card.icon}
                  </div>
                  <div>
                    <h3 className="sc-card-title">{card.title}</h3>
                    <p className="sc-card-desc">{card.desc}</p>
                  </div>
                </div>
                <ul className="sc-item-list">
                  {card.items.map((item, ii) => (
                    <li key={ii} className="sc-item">
                      <span className="sc-item-icon">{item.icon}</span>
                      <span>{item.text}</span>
                    </li>
                  ))}
                </ul>
                {card.cta && (
                  <Link to={card.cta.to} className="sc-card-link" style={{ color: card.color }}>
                    {card.cta.label} <span>→</span>
                  </Link>
                )}
                <div className="sc-card-glow" style={{ background: card.color }} aria-hidden />
                <div className="sc-card-top-line" style={{ background: `linear-gradient(90deg, ${card.color}, transparent)` }} aria-hidden />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ COMMUNITY ════════════════════ */}
      <section className="sc-community-section">
        <div className="sc-container">
          <SectionHeader
            eyebrow="💬 Join the IncogniFlex Community"
            title="Connect. Learn."
            accent="Grow Together."
            sub="A thriving space where builders, operators, and AI enthusiasts share, collaborate, and shape what comes next."
          />
          <div className="sc-community-grid">
            {communityCards.map((card, i) => (
              <div key={i} className="sc-community-card" style={{ '--cc': card.color }}>
                <div className="sc-comm-icon-wrap" style={{ borderColor: card.color + '44', background: card.color + '15', color: card.color }}>
                  {card.icon}
                </div>
                <h3 className="sc-card-title">{card.title}</h3>
                <p className="sc-card-desc">{card.desc}</p>
                <Link to={card.cta.to} className="sc-card-link" style={{ color: card.color }}>
                  {card.cta.label} <span>→</span>
                </Link>
                <div className="sc-card-glow" style={{ background: card.color }} aria-hidden />
                <div className="sc-card-top-line" style={{ background: `linear-gradient(90deg, ${card.color}, transparent)` }} aria-hidden />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ PROGRAMS ════════════════════ */}
      <section className="sc-programs-section">
        <div className="sc-container">
          <SectionHeader
            eyebrow="🧑‍🤝‍🧑 Community Programs"
            title="Built for"
            accent="Power Users"
            sub="Go deeper with IncogniFlex — join a program designed to reward expertise, foster collaboration, and multiply impact."
          />
          <div className="sc-programs-grid">
            {programCards.map((card, i) => (
              <div key={i} className="sc-program-card" style={{ '--cc': card.color }}>
                <div className="sc-prog-badge">{card.badge}</div>
                <div className="sc-prog-header">
                  <div className="sc-prog-icon-wrap" style={{ borderColor: card.color + '44', background: card.color + '15', color: card.color }}>
                    {card.icon}
                  </div>
                  <div>
                    <h3 className="sc-prog-title">{card.title}</h3>
                    <span className="sc-prog-sub" style={{ color: card.color }}>{card.sub}</span>
                  </div>
                </div>
                <p className="sc-card-desc">{card.desc}</p>
                <ul className="sc-highlights">
                  {card.highlights.map((h, hi) => (
                    <li key={hi} className="sc-highlight">
                      <span className="sc-highlight-dot" style={{ background: card.color }} />
                      {h}
                    </li>
                  ))}
                </ul>
                {card.cta && (
                  <Link to={card.cta.to} className="sc-prog-cta-btn" style={{ '--cc': card.color }}>
                    {card.cta.label} <span>→</span>
                  </Link>
                )}
                <div className="sc-card-glow" style={{ background: card.color }} aria-hidden />
                <div className="sc-card-top-line" style={{ background: `linear-gradient(90deg, ${card.color}, transparent)` }} aria-hidden />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ CTA ════════════════════ */}
      <section className="sc-cta-section">
        <div className="sc-cta-glow" aria-hidden />
        <div className="sc-cta-grid-bg" aria-hidden />
        <div className="sc-container">
          <div className="sc-cta-inner">
            <span className="sc-cta-eyebrow">
              <span className="sc-eyebrow-dot" />
              Start Today
            </span>
            <h2 className="sc-cta-title">
              Ready to <span className="sc-accent">Get Support?</span>
            </h2>
            <p className="sc-cta-sub">
              Whether you're stuck on an integration or looking to join our community of builders — we're here.
            </p>
            <div className="sc-cta-actions">
              <Link to="/contact" className="sc-cta-btn primary">
                <span>Talk to an Expert</span>
                <span className="sc-btn-arrow">→</span>
              </Link>
              <Link to="/docs" className="sc-cta-btn secondary">Browse Help Center</Link>
              {/* <Link to="/community" className="sc-cta-btn secondary">Join Community</Link> */}
            </div>
            <p className="sc-cta-note">No commitment required · Response within 24 hours</p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default SupportCommunity;